import { ArrowLeft, Check } from 'lucide-react'

import { t } from '../../../common/i18n.js'
import { UrlLinkQrInput } from '../../../common/models/url-link.js'
import { Language } from '../../../common/types/i18n.js'
import {
    UrlAccessLogSelect,
    UrlLinkBrand,
    UrlLinkDotStyle,
    UrlLinkLogo,
    UrlLinkSelect,
} from '../../../server/database/types.js'
import { PageHeader } from '../../components/page-header/page-header.js'
import { PageShell } from '../../components/page-shell/page-shell.js'
import { deviceTypeLabel } from './url-shortener-helpers.js'

const BRAND_DOMAINS: Record<UrlLinkBrand, string> = {
    play: 'url.play.ee',
    nope: 'url.nope.ee',
}

export interface AccessLogStats {
    totalHits: number
    uniqueIps: number
    realHits: number
    botHits: number
    deviceBreakdown: Record<string, number>
}

export interface UrlShortenerDetailViewProps {
    lang: Language
    link: UrlLinkSelect | undefined
    isLoading: boolean
    isNotFound: boolean
    isSavingQr: boolean
    logs: UrlAccessLogSelect[]
    logsTotal: number
    _logsPage: number
    logsLoading: boolean
    stats: AccessLogStats | undefined
    qrSvgUrl: string
    onBack: () => void
    onSaveQr: (settings: UrlLinkQrInput) => void
    onDownloadQr: () => void
    onLoadMoreLogs: () => void
}

export const UrlShortenerDetailView = ({
    lang,
    link,
    isLoading,
    isNotFound,
    isSavingQr,
    logs,
    logsTotal,
    _logsPage,
    logsLoading,
    stats,
    qrSvgUrl,
    onBack,
    onSaveQr,
    onDownloadQr,
    onLoadMoreLogs,
}: UrlShortenerDetailViewProps) => {
    if (isLoading && !link) {
        return (
            <PageShell>
                <div className="url-shortener-detail">…</div>
            </PageShell>
        )
    }

    if (isNotFound || !link) {
        return (
            <PageShell>
                <button className="url-shortener-detail__back" onClick={onBack} type="button">
                    <ArrowLeft size={14} /> {t.urlShortener.backToList(lang)}
                </button>
                <p>{t.urlShortener.notFound(lang)}</p>
            </PageShell>
        )
    }

    const domain = BRAND_DOMAINS[link.brand]
    const fullShortUrl = `https://${domain}/${link.short_code}`

    const handleQrSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const form = e.currentTarget
        const data = Object.fromEntries(
            new FormData(form) as unknown as Iterable<readonly [PropertyKey, string]>,
        ) as Record<string, string>
        onSaveQr({
            qr_fg_color: data.qr_fg_color,
            qr_bg_color: data.qr_bg_color,
            qr_dot_style: data.qr_dot_style as UrlLinkDotStyle,
            qr_dot_radius: Number(data.qr_dot_radius),
            qr_logo: data.qr_logo as UrlLinkLogo,
        })
    }

    const hasMoreLogs = logs.length < logsTotal

    return (
        <PageShell>
            <button className="url-shortener-detail__back" onClick={onBack} type="button">
                <ArrowLeft size={14} /> {t.urlShortener.backToList(lang)}
            </button>

            <PageHeader title={link.title ?? link.short_code} />

            <div className="url-shortener-detail__header">
                <a
                    className="url-shortener-detail__short-url"
                    href={fullShortUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {fullShortUrl}
                </a>
                <p className="url-shortener-detail__original-url">{link.original_url}</p>
            </div>

            {/* Stats strip */}
            {stats && (
                <div className="url-shortener-detail__stats">
                    <StatCard
                        label={t.urlShortener.totalHits(lang)}
                        value={String(stats.totalHits)}
                    />
                    <StatCard
                        label={t.urlShortener.uniqueIps(lang)}
                        value={String(stats.uniqueIps)}
                    />
                    <StatCard
                        label={t.urlShortener.realTraffic(lang)}
                        value={String(stats.realHits)}
                    />
                    <StatCard
                        label={t.urlShortener.botTraffic(lang)}
                        value={String(stats.botHits)}
                    />
                    {Object.entries(stats.deviceBreakdown).map(([device, count]) => (
                        <StatCard
                            key={device}
                            label={deviceTypeLabel(device, lang)}
                            value={String(count)}
                        />
                    ))}
                </div>
            )}

            <div className="url-shortener-detail__columns">
                {/* QR Customizer */}
                <div className="url-shortener-detail__qr-panel">
                    <h3 className="url-shortener-detail__section-title">
                        {t.urlShortener.qrCustomizer(lang)}
                    </h3>

                    {qrSvgUrl && (
                        <img
                            className="url-shortener-detail__qr-preview"
                            src={qrSvgUrl}
                            alt={t.urlShortener.qrPreviewAlt(lang)}
                        />
                    )}

                    <form className="url-shortener-detail__qr-form" onSubmit={handleQrSubmit}>
                        <div className="url-shortener-detail__form-row">
                            <label className="url-shortener-detail__label">
                                {t.urlShortener.fgColor(lang)}
                            </label>
                            <input
                                type="color"
                                name="qr_fg_color"
                                defaultValue={link.qr_fg_color}
                                className="url-shortener-detail__color-input"
                            />
                        </div>
                        <div className="url-shortener-detail__form-row">
                            <label className="url-shortener-detail__label">
                                {t.urlShortener.bgColor(lang)}
                            </label>
                            <input
                                type="color"
                                name="qr_bg_color"
                                defaultValue={link.qr_bg_color}
                                className="url-shortener-detail__color-input"
                            />
                        </div>
                        <div className="url-shortener-detail__form-row">
                            <label className="url-shortener-detail__label">
                                {t.urlShortener.dotStyle(lang)}
                            </label>
                            <select
                                name="qr_dot_style"
                                defaultValue={link.qr_dot_style}
                                className="url-shortener-detail__select"
                            >
                                <option value="square">
                                    {t.urlShortener.dotStyleSquare(lang)}
                                </option>
                                <option value="circle">
                                    {t.urlShortener.dotStyleCircle(lang)}
                                </option>
                            </select>
                        </div>
                        <div className="url-shortener-detail__form-row">
                            <label className="url-shortener-detail__label">
                                {t.urlShortener.dotRadius(lang)}
                            </label>
                            <input
                                type="range"
                                name="qr_dot_radius"
                                min="0"
                                max="1"
                                step="0.05"
                                defaultValue={String(link.qr_dot_radius)}
                                className="url-shortener-detail__range"
                            />
                        </div>
                        <div className="url-shortener-detail__form-row">
                            <label className="url-shortener-detail__label">
                                {t.urlShortener.logo(lang)}
                            </label>
                            <select
                                name="qr_logo"
                                defaultValue={link.qr_logo}
                                className="url-shortener-detail__select"
                            >
                                <option value="none">{t.urlShortener.logoNone(lang)}</option>
                                <option value="play">{t.urlShortener.logoPlay(lang)}</option>
                                <option value="nope">{t.urlShortener.logoNope(lang)}</option>
                                <option value="empty">{t.urlShortener.logoEmpty(lang)}</option>
                            </select>
                        </div>

                        <div className="url-shortener-detail__qr-actions">
                            <button
                                className="url-shortener-detail__save-btn"
                                type="submit"
                                disabled={isSavingQr}
                            >
                                {isSavingQr
                                    ? t.urlShortener.savingQr(lang)
                                    : t.urlShortener.saveQr(lang)}
                            </button>
                            <button
                                className="url-shortener-detail__download-btn"
                                type="button"
                                onClick={onDownloadQr}
                                disabled={!qrSvgUrl}
                            >
                                {t.urlShortener.downloadQr(lang)}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Access log table */}
                <div className="url-shortener-detail__logs">
                    <h3 className="url-shortener-detail__section-title">
                        {t.urlShortener.accessLog(lang)}
                        {logsTotal > 0 && (
                            <span className="url-shortener-detail__log-count"> ({logsTotal})</span>
                        )}
                    </h3>

                    {logsLoading && logs.length === 0 ? (
                        <div>…</div>
                    ) : logs.length === 0 ? (
                        <p className="url-shortener-detail__no-logs">
                            {t.urlShortener.noLogs(lang)}
                        </p>
                    ) : (
                        <>
                            <table className="url-shortener-detail__log-table">
                                <thead>
                                    <tr>
                                        <th>{t.urlShortener.logDate(lang)}</th>
                                        <th>{t.urlShortener.logIp(lang)}</th>
                                        <th>{t.urlShortener.logDevice(lang)}</th>
                                        <th>{t.urlShortener.logBot(lang)}</th>
                                        <th>{t.urlShortener.logReferer(lang)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log) => (
                                        <tr key={log.id} className="url-shortener-detail__log-row">
                                            <td>{log.accessed_at_utc.slice(0, 16)}</td>
                                            <td>{log.ip_address ?? '—'}</td>
                                            <td>{deviceTypeLabel(log.device_type, lang)}</td>
                                            <td>{log.is_bot ? <Check size={14} /> : '—'}</td>
                                            <td
                                                className="url-shortener-detail__log-referer"
                                                title={log.referer ?? ''}
                                            >
                                                {log.referer ?? '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {hasMoreLogs && (
                                <button
                                    className="url-shortener-detail__load-more"
                                    onClick={onLoadMoreLogs}
                                    disabled={logsLoading}
                                    type="button"
                                >
                                    {t.urlShortener.loadMore(lang)}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </PageShell>
    )
}

const StatCard = ({ label, value }: { label: string; value: string }) => (
    <div className="url-shortener-detail__stat-card">
        <div className="url-shortener-detail__stat-value">{value}</div>
        <div className="url-shortener-detail__stat-label">{label}</div>
    </div>
)
